import { useState } from "react";
import { toastService } from "@services/ToastService";
import { programService } from "@services/ProgramService";
import { ProgramPayload } from "@shared/interfaces/ProgramFiles";

export const useProgram = () => {
  const [loading, setLoading] = useState(false);

  const saveProgram = async (
    payload: ProgramPayload,
    options?: {
      programId?: string;
    }
  ) => {
    setLoading(true);
    try {
      const response = options?.programId
        ? await programService.updateProgram(options.programId, payload)
        : await programService.createProgram(payload);

      toastService.success(
        `Program ${options?.programId ? "updated" : "created"} successfully`
      );

      return response;
    } catch (error) {
      toastService.error("Failed to save program");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchPrograms = async (
    mine: boolean,
    params?: {
      order?: string;
      offset?: number;
      limit?: number;
    }
  ) => {
    setLoading(true);
    try {
      const response = mine
        ? await programService.getMyPrograms(params)
        : await programService.getAllPrograms(params);
      return response || [];
    } catch (error) {
      console.error("Failed to fetch programs");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteProgram = async (programId: number | string) => {
    setLoading(true);
    try {
      await programService.deleteProgram(programId);
      toastService.success("Program deleted");
    } catch (error) {
      toastService.error("Failed to delete program");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getProgramById = async (programId: string | number) => {
    try {
      return await programService.getProgramById(programId);
    } catch (error) {
      console.error("Failed to fetch program");
      throw error;
    }
  };

  const getProgramsLatestUpdates = async (programId: string | number) => {
    try {
      return await programService.getProgramsLatestUpdates(programId);
    } catch (error) {
      console.error("Failed to fetch latest updates");
      throw error;
    }
  };

  // --- Suggestion API handlers ---

  const getSuggestions = async (
    programId: string | number,
    programUpdateId?: number
  ) => {
    try {
      return await programService.getSuggestions(programId, programUpdateId);
    } catch (error) {
      console.error("Failed to fetch suggestions");
      throw error;
    }
  };

  const applySuggestion = async (
    programId: string | number,
    suggestionId: number
  ) => {
    try {
      await programService.applySuggestion(programId, suggestionId);
      toastService.success("Suggestion applied successfully");
    } catch (error) {
      toastService.error("Failed to apply suggestion");
      throw error;
    }
  };

  const ignoreSuggestion = async (
    programId: string | number,
    suggestionId: number
  ) => {
    try {
      await programService.ignoreSuggestion(programId, suggestionId);
      toastService.success("Suggestion ignored");
    } catch (error) {
      toastService.error("Failed to ignore suggestion");
      throw error;
    }
  };

  const applyAllSuggestions = async (
    programId: string | number,
    programUpdateId: number
  ) => {
    try {
      await programService.applyAllSuggestions(programId, programUpdateId);
      toastService.success("All suggestions applied successfully");
    } catch (error) {
      toastService.error("Failed to apply all suggestions");
      throw error;
    }
  };

  const ignoreAllSuggestions = async (
    programId: string | number,
    programUpdateId: number
  ) => {
    try {
      await programService.ignoreAllSuggestions(programId, programUpdateId);
      toastService.success("All suggestions ignored");
    } catch (error) {
      toastService.error("Failed to ignore all suggestions");
    }
  };

  const getProgramsDropdown = async (query?: string) => {
    try {
      const response = await programService.getProgramsDropdown();
      return response;
    } catch (error) {
      console.error("Failed to fetch projects dropdown");
      throw error;
    }
  };

  const getProgramUpdatesByType = async (params: {
    programId: string | number;
    type: "goal" | "milestone";
    ids?: string[];
    limit?: number;
  }) => {
    try {
      return await programService.getProgramUpdatesByType(params);
    } catch (error) {
      console.error("Failed to fetch updates by type");
      throw error;
    }
  };

  return {
    loading,
    saveProgram,
    fetchPrograms,
    deleteProgram,
    getProgramById,
    getProgramsLatestUpdates,
    getSuggestions,
    applySuggestion,
    ignoreSuggestion,
    applyAllSuggestions,
    ignoreAllSuggestions,
    getProgramsDropdown,
    getProgramUpdatesByType,
  };
};
